import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { sendMessage } from '@/features/chat/chatSlice';
import { updateOrderItem, selectOrderItems, OrderItem } from '@/features/order/orderSlice';
import { DynamicTable } from './DynamicTable';
import { Login } from '../auth/Login';
import { Register } from '../auth/Register';
import OrderConfirmationCard from './OrderConfirmationCard';
import { ImageUploader } from '../order/ImageUploader';
import OrderSummary from '../order/OrderSummary';
import { OrderItemEditor, OrderItemData } from '../order/OrderItemEditor';
import DefaultCanvas from './DefaultCanvas';

interface DynamicComponentProps {
  uiPayload: Record<string, any>;
}

export const DynamicComponent = ({ uiPayload }: DynamicComponentProps) => {
  const dispatch = useAppDispatch();
  const orderItems = useAppSelector(selectOrderItems);
  const name = uiPayload?.name;
  const props = uiPayload?.props || {};

  console.log("Rendering DynamicComponent with payload:", uiPayload);

  switch (name) {
    case 'RequestLogin':
      return <Login />;
    case 'table':
      return <DynamicTable columns={props.columns} rows={props.rows} itemType={props.itemType} />;
    case 'register':
      return <Register />;
    case 'orderConfirmation':
      return <OrderConfirmationCard {...props} />;
    case 'OrderSummary': {
      const item = orderItems.find(i => i.id === props.imageId);
      const summaryProps = { ...props, ...item };
      return <OrderSummary {...summaryProps} />;
    }
    case 'RequestImageUpload':
    case 'imageUploader': {
            const handleUploadSuccess = (
        uploadedFileId: string,
        fileName: string,
        originalWidthPx?: number,
        originalHeightPx?: number
      ) => {
        // Do not update state directly. Instead, send the data to the AI.
        const payload = {
          uploadedFileId,
          fileName,
          originalWidthPx,
          originalHeightPx,
        };
        // Notify the AI that the upload was successful so it can update the state.
        dispatch(
          sendMessage({
            userFacingMessage: `Uploaded ${payload.fileName}.`,
            internalMessage: {
              event: 'FILE_UPLOAD_SUCCESS',
              details: payload,
            },
          })
        );
      };


      const handleUploadError = (errorMessage: string) => {
        // Send a silent message to the AI about the error
        dispatch(
          sendMessage({
            userFacingMessage: 'There was an error uploading the file.',
            internalMessage: `INTERNAL_ACTION:FILE_UPLOAD_ERROR:${errorMessage}`,
          })
        );
      };

            return <ImageUploader onUploadSuccess={handleUploadSuccess} onUploadError={handleUploadError} />;
    }
    case 'orderItemEditor': {
      const handleSubmit = (itemData: OrderItemData) => {
        const uploadedFileId = props.uploadedFileId;
        // Update the item in our local order state
                dispatch(updateOrderItem({ uploadedFileId, data: itemData as Partial<OrderItem> }));

        const payload = { ...itemData, uploadedFileId };
        // Send a silent message to the AI with the configured item data
        dispatch(
          sendMessage({
            userFacingMessage: 'I have configured the item.',
            internalMessage: `INTERNAL_ACTION:ORDER_ITEM_CONFIGURED:${JSON.stringify(
              payload
            )}`,
          })
        );
      };

      const handleCancel = () => {
        // Notify the AI that the user cancelled the configuration
        dispatch(
          sendMessage({
            userFacingMessage: 'I have cancelled the configuration.',
            internalMessage: 'INTERNAL_ACTION:ORDER_ITEM_CANCELLED',
          })
        );
      };

      return <OrderItemEditor uploadedFileId={props.uploadedFileId} onSubmit={handleSubmit} onCancel={handleCancel} />;
    }
    default:
      if (!name) {
        // If no component is specified, render the default canvas. This is the default state during conversation.
        return <DefaultCanvas />;
      }
      // If a component is specified but not supported, show an error. This helps debugging.
      return <div>Unsupported component type: {name}</div>;
  }
};
